import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useAccountBookStore } from '@/store/accountBookStore';

/**
 * 시간대별 인사 + 오늘 요약 카드
 * 디자인 출처: Wanted DS Green 대시보드 (GreetingHero, 마스코트 제거 버전)
 *
 * 표시 데이터는 모두 store에서 derive — 새 데이터 fetch 없음.
 * 폰트 사이즈는 40대 이상 사용자 가독성을 위해 상향.
 */

const formatNumber = (n: number) => n.toLocaleString('ko-KR');

const GreetingHero: React.FC = () => {
  const { dashboardData, currentAccountBook } = useAccountBookStore();

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 6) return '이른 새벽까지 고생이세요';
    if (hour < 11) return '좋은 아침이에요';
    if (hour < 18) return '오늘도 수고 많으세요';
    if (hour < 22) return '오늘 하루도 고생하셨어요';
    return '늦은 시간까지 수고하세요';
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${days[d.getDay()]}요일`;
  }, []);

  const businessName =
    currentAccountBook?.businessName || currentAccountBook?.name || '사장';
  const ownerName = `${businessName}님`;

  const profit = dashboardData.kpiData?.netIncome ?? 0;
  const prevProfit = dashboardData.previousYearKpiData?.netIncome ?? 0;
  const deltaPct =
    prevProfit > 0
      ? Math.round(((profit - prevProfit) / prevProfit) * 100)
      : profit > 0
      ? 100
      : 0;
  const deltaPositive = deltaPct >= 0;
  const hasComparison = prevProfit !== 0 || profit !== 0;

  return (
    <Box
      sx={{
        animation: 'fadeUp 400ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards',
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* 캔버스 위 대형 인사 타이포 + 날짜 필 (Quixotic 'Welcome Back, Sujon' 패턴) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2.5,
          px: 0.5,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: 'var(--font-display)',
            fontSize: { xs: 26, sm: 34 },
            fontWeight: 800,
            letterSpacing: '-0.024em',
            color: 'var(--fg-strong)',
            lineHeight: 1.25,
            m: 0,
          }}
        >
          {greeting},{' '}
          <Box component="span" sx={{ color: 'var(--fg-tertiary)', fontWeight: 700 }}>
            {ownerName}
          </Box>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'var(--bg-base)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-pill)',
            px: 2,
            py: 0.875,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--fg-secondary)',
              letterSpacing: '0.014em',
              whiteSpace: 'nowrap',
            }}
          >
            {today}
          </Typography>
        </Box>
      </Box>

      {/* 올해 요약 카드 — 영업이익 문장 + 작년 100% 기준 진행 막대 */}
      <Paper
        elevation={0}
        sx={{
          background: 'var(--bg-base)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-24)',
        }}
      >
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
          }}
        >
        <Typography
          sx={{
            mt: 0,
            fontSize: { xs: 17, sm: 19 },
            color: 'var(--fg-secondary)',
            letterSpacing: '0.014em',
            lineHeight: 1.6,
          }}
        >
          {hasComparison ? (
            <>
              {dashboardData.selectedYear}년 영업이익은{' '}
              <Box
                component="strong"
                sx={{
                  color: 'var(--fg-strong)',
                  fontWeight: 800,
                  fontFamily: 'var(--font-numeric)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: { xs: 22, sm: 26 },
                  letterSpacing: '-0.012em',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatNumber(profit)}
              </Box>
              {prevProfit > 0 && (
                <>
                  , 전년 대비{' '}
                  <Box
                    component="strong"
                    sx={{
                      color: deltaPositive
                        ? 'var(--c-green-700)'
                        : 'var(--c-red-600)',
                      fontWeight: 800,
                      fontSize: { xs: 18, sm: 20 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {deltaPositive ? '▲' : '▼'} {Math.abs(deltaPct)}%
                  </Box>
                </>
              )}
            </>
          ) : (
            '아직 올해 데이터가 없어요. 거래를 기록하면 요약이 나타납니다.'
          )}
        </Typography>

        {/* 작년 영업이익을 100% 기준선으로 두고 채워가는 진행 막대.
            작년이 0이거나 음수면 비교 의미 없음 → 표시 안 함. */}
        {prevProfit > 0 && (() => {
          const progress = Math.max(0, Math.round((profit / prevProfit) * 100));
          const reached = progress >= 100;
          // 막대 스케일: 100% 또는 현재 진행도 + 여유 20% 중 큰 값
          const scale = Math.max(120, progress + 20);
          const fillPct = Math.min(100, (progress / scale) * 100);
          const baselinePct = (100 / scale) * 100;
          const barColor = reached ? 'var(--primary)' : 'var(--c-orange-600)';
          const labelColor = reached ? 'var(--c-green-700)' : 'var(--c-orange-600)';

          return (
            <Box sx={{ mt: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'var(--fg-tertiary)',
                    letterSpacing: '0.014em',
                  }}
                >
                  작년 영업이익 100% 기준 채움
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'var(--font-numeric)',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: { xs: 20, sm: 24 },
                    fontWeight: 800,
                    color: labelColor,
                    letterSpacing: '-0.012em',
                  }}
                >
                  {progress}%
                </Typography>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  height: 12,
                  background: 'var(--c-cool-200)',
                  borderRadius: 'var(--r-pill)',
                  overflow: 'hidden',
                }}
              >
                {/* 진행 채움 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${fillPct}%`,
                    background: barColor,
                    borderRadius: 'var(--r-pill)',
                    transition: 'width 1200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                  }}
                />
                {/* 100% 기준선 마커 — 작년 위치 표시 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${baselinePct}%`,
                    top: -3,
                    bottom: -3,
                    width: 2,
                    background: 'var(--fg-strong)',
                    opacity: 0.55,
                    borderRadius: 1,
                  }}
                  title="작년 영업이익 100% 지점"
                />
              </Box>
              <Typography
                sx={{
                  mt: 1,
                  fontSize: 13,
                  color: 'var(--fg-tertiary)',
                  letterSpacing: '0.014em',
                }}
              >
                작년 동기간:{' '}
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'var(--font-numeric)',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                    color: 'var(--fg-secondary)',
                  }}
                >
                  {formatNumber(prevProfit)}
                </Box>
              </Typography>
            </Box>
          );
        })()}
        </Box>
      </Paper>
    </Box>
  );
};

export default GreetingHero;
